import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import PhoneInTalkRounded from '@mui/icons-material/PhoneInTalkRounded';
import {
  EmptyState, ListTable, Panel, SearchField, SegmentedControl, StatusChip, ToolbarButton,
  type ListColumn,
} from '../../../ui-kit';
import {
  callTheCarrier, fetchCcCases, watchCases, type CcCase,
} from '../../../services/carrierCorrespondenceService';
import { matches, useLive } from '../rocco/live';
import { CALLING, SIDE, ageTone, capital, expectedText, notAnswered, searchable, stalledFor } from './status';
import CaseStory from './CaseStory';

/**
 * Every case with a carrier, longest-stalled first: the order to work them in.
 *
 * Built exactly as the other agents' lists are: search and one segmented
 * control in the header, live state in the subtitle, the table in the panel's
 * own padding. Six columns, because that is what fits the width the other
 * lists fit; a seventh pushes the call button off the right edge.
 *
 * There is no separate state column. Each row already says it three ways — the
 * button, what is outstanding (or "not called yet"), and whose move it is, so a
 * chip saying the same thing again only cost the room the call button needs.
 */
export default function CasesPage() {
  const { data, error, loading, streaming, reconnecting } = useLive(
    fetchCcCases, 20_000, { watch: watchCases });
  const [query, setQuery] = React.useState('');
  const [show, setShow] = React.useState<'all' | 'stalled' | 'answered'>('all');
  const [open, setOpen] = React.useState<string | null>(null);
  const [ringing, setRinging] = React.useState<string | null>(null);
  const [problem, setProblem] = React.useState<string | null>(null);

  const all = data?.cases ?? [];
  const stalled = all.filter((c) => c.status === 'waiting').length;
  const answered = all.filter((c) => c.status === 'answered').length;
  const rows = all
    .filter((row) => (show === 'stalled' ? row.status === 'waiting'
      : show === 'answered' ? row.status === 'answered' : true))
    .filter((row) => matches(query, searchable(row)));

  // From the row itself: picking which carrier to ring is the job of this list.
  const ring = async (id: string) => {
    setRinging(id);
    setProblem(null);
    try {
      await callTheCarrier(id);
      setOpen(id);   // the case opens, so the call can be watched
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not start the call');
    } finally {
      setRinging(null);
    }
  };

  const columns: ListColumn<CcCase>[] = [
    {
      key: 'stalled',
      header: 'Stalled for',
      width: 104,
      sortValue: (row) => row.days_stalled,
      sortStartsDescending: true,
      render: (row) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({
            fontWeight: 600,
            color: ageTone(row.days_stalled) === 'bad' ? t.tokens.semantic.danger
              : ageTone(row.days_stalled) === 'warning' ? t.tokens.semantic.warning
                : t.tokens.text.primary,
          })}>
            {stalledFor(row.days_stalled)}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            since {row.stage_since
              ? new Date(row.stage_since).toLocaleDateString([], { day: 'numeric', month: 'short' })
              : '—'}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'case',
      header: 'Case',
      grow: 1.1,
      minWidth: 160,
      sortValue: (row) => row.applicant,
      render: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
            {row.applicant}
          </Typography>
          <Typography variant="caption" sx={(t) => ({
            color: t.tokens.text.tertiary, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere',
          })}>
            {row.policy_num} · {row.carrier}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'stage',
      header: 'Stuck at',
      grow: 1,
      minWidth: 140,
      sortValue: (row) => row.stage,
      render: (row) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, overflowWrap: 'anywhere' })}>
          {row.stage}
        </Typography>
      ),
    },
    {
      key: 'outstanding',
      header: 'What is outstanding',
      grow: 1.4,
      minWidth: 170,
      sortValue: (row) => row.blocking_item ?? row.reason ?? '',
      render: (row) => <Outstanding row={row} />,
    },
    {
      key: 'side',
      header: 'Waiting on',
      width: 196,
      sortValue: (row) => row.blocking_side ?? '',
      render: (row) => (row.blocking_side
        ? <StatusChip {...SIDE[row.blocking_side]} />
        : <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>—</Typography>),
    },
    {
      key: 'call',
      header: '',
      width: 244,
      align: 'right',
      // While a call is running its button is replaced by what the call is
      // doing, so the row says it is busy rather than offering a dead button.
      render: (row) => (row.status === 'calling' || row.status === 'call_ended' ? (
        <StatusChip tone="good" pulse label={CALLING[row.status]} />
      ) : (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {/* Calling the client is not built yet: the button is here to show
              where it will go, and does nothing for now. */}
          <ToolbarButton icon={<PhoneInTalkRounded />} onClick={() => undefined}>
            Call Client
          </ToolbarButton>
          <ToolbarButton
            icon={<PhoneInTalkRounded />}
            onClick={() => { void ring(row.id); }}
            disabled={ringing === row.id}
          >
            Call Carrier
          </ToolbarButton>
        </Stack>
      )),
    },
  ];

  const controls = (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
      <Box sx={{ width: 220 }}>
        <SearchField value={query} onChange={setQuery} placeholder="Search cases…" />
      </Box>
      <SegmentedControl
        value={show}
        onChange={setShow}
        ariaLabel="Which cases"
        options={[
          { value: 'all', label: `All · ${all.length}` },
          { value: 'stalled', label: `Stalled · ${stalled}` },
          { value: 'answered', label: `Answered · ${answered}` },
        ]}
      />
    </Stack>
  );

  const table = (
    <ListTable
      columns={columns}
      rows={rows}
      getRowKey={(row) => row.id}
      pageSize={25}
      loading={loading}
      onRowClick={(row) => setOpen(row.id)}
      emptyMessage={all.length
        ? 'Nothing matches. Clear the search or the filter.'
        : 'No cases with a carrier. Everything is moving.'}
    />
  );

  const state = reconnecting ? 'Reconnecting…' : streaming ? 'live' : 'updates by itself';

  return (
    <Stack spacing={2}>
      <Panel
        title="Cases"
        subtitle={problem ?? `${stalled} stalled · ${answered} answered by the AI · longest first · ${state}`}
        loading={loading}
        loadingHeight={420}
        actions={controls}
        expanded={{ content: table, actions: controls }}
      >
        {error && !data ? <EmptyState message={error} /> : null}
        {error && !data ? null : table}
      </Panel>
      {open ? <CaseStory caseId={open} onClose={() => setOpen(null)} /> : null}
    </Stack>
  );
}

/** What the carrier said, or why there is nothing yet. One line, one caption. */
function Outstanding({ row }: { row: CcCase }) {
  if (row.status === 'calling' || row.status === 'call_ended') {
    return (
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.semantic.success })}>
        {row.status === 'calling' ? 'On the phone with the carrier' : 'Reading the call'}
      </Typography>
    );
  }
  if (row.status === 'needs_person') {
    return (
      <Stack sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.semantic.danger })}>Needs a person</Typography>
        {row.reason ? (
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
            {row.reason}
          </Typography>
        ) : null}
      </Stack>
    );
  }
  if (row.blocking_item || row.reason) {
    return (
      <Stack sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
          {capital(row.blocking_item || row.reason)}
        </Typography>
        {row.expected_by ? (
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
            {expectedText(row.carrier, row.expected_by)}
          </Typography>
        ) : null}
      </Stack>
    );
  }
  return (
    <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{notAnswered(row)}</Typography>
  );
}
